// ============================================================
// TMR Portal Admin — the Empresa contract, as the backend actually sends it
// ============================================================
import type { BankAccountDetailsInput } from '../finance/contract';

/**
 * How far the Empresa has got in being replicated to the legacy system (04 §3).
 *
 * This is *not* the business status — that is {@link CompanyBusinessStatus}, a different question about the
 * same Empresa. This one is only about the copy in the legacy, and it is what the operator can act on: a
 * failed export is work waiting to be done.
 *
 * It also cannot say, on its own, which way the record travelled: an Empresa registered here and pushed over
 * and one brought in from the legacy are both `Exported`. {@link RecordOrigin} is what separates them, and
 * the screens read the two together — see `syncState` in `./status`.
 */
export type ExportStatus = 'Pending' | 'Exported' | 'Failed';

/**
 * Where the Empresa's record came from (10 B6d) — registered in tmr_v2, or imported from the legacy.
 *
 * Fixed when the record is created and never changed, which is why it is its own field: editing an imported
 * Empresa walks its {@link ExportStatus} back through `Pending`, so an "Imported" living in that type would
 * be erased by the first edit.
 */
export type RecordOrigin = 'Onboarded' | 'Imported';

/**
 * Where the Empresa is in its business lifecycle (10 B6a) — the status the portal leads with, because it is
 * the one the operator works against day to day.
 *
 * Until M3 there is no state machine: the contract → signature → completed transitions arrive with the
 * Contracts module, and until then staff move the status by hand through `PATCH /companies/{id}/status`.
 *
 * `Inactive` is the one with an effect outside the portal: it is replicated as `produtores.ativo = false`,
 * which takes the Empresa out of every legacy listing, and the server refuses new contracts for it.
 */
export type CompanyBusinessStatus =
  | 'PendingContract'
  | 'PendingAccountTest'
  | 'AwaitingSignature'
  | 'Completed'
  | 'Rejected'
  | 'Inactive';

/** One Empresa in the list — what the table needs, never the whole record (`GET /companies`). */
export interface CompanyListItem {
  id: string;
  name: string;
  /**
   * Normalized, without a mask — format it for display, never send it back masked.
   *
   * **Null when the record has none** (19 §5): the legacy has producers the V1 never made type one, and
   * the backfill brings them across rather than leaving the portfolio half here.
   */
  cnpj: string | null;
  subdomain: string;
  /**
   * The registered address, whole — the same shape the detail returns.
   *
   * It used to be the city and the UF alone, which is why the map opened from the list reported "endereço
   * completo não cadastrado" for Empresas that had one: the address was never missing, it was never sent.
   */
  address: CompanyAddress;
  /** The business lifecycle status — the badge the list leads with. */
  status: CompanyBusinessStatus;
  exportStatus: ExportStatus;
  recordOrigin: RecordOrigin;
  /** Gestor de Venda, resolved by the backend for display; null when none is assigned. */
  salesManager: CompanyManager | null;
  /** Gestor de Pós-venda, on the same terms. */
  postSalesManager: CompanyManager | null;
  /** Producer id in the legacy system; null until the export succeeds. */
  legacyProducerId: number | null;
  /** What this registration is missing (19 §5); empty when it is complete. */
  registrationGaps: RegistrationGap[];
  createdAt: string;
}

/**
 * A person on the Empresa's record — a Party in the Identity module, referenced by id (10 B8.4) and
 * expanded here for display.
 *
 * Only `id` is guaranteed. The details are read from the directory at request time, so a reference the
 * directory no longer resolves arrives with them null — deliberately, so a dangling reference looks like
 * one instead of like an Empresa with a blank representative. Screens must render that case.
 */
export interface PartyReference {
  id: string;
  name: string | null;
  /** Normalized digits, without a mask. */
  cpf: string | null;
  email: string | null;
  phone: string | null;
}

/**
 * A person as the registration submits them: **either** the id of somebody the directory already holds,
 * **or** the details to file them with (10 B8.4).
 *
 * Both forms exist because both are real. An operator who can read the directory picks a person and the
 * screen sends `partyId`; an operator who cannot — or who is registering somebody nobody has filed yet —
 * sends the details, and the backend matches or files the person before storing the reference. Requiring
 * the id in every case would have left an operator holding `company.onboard` unable to register an
 * Empresa at all.
 *
 * The screen sends one form or the other, never both: {@link CompanyForm} drops the picked id the moment
 * the operator edits a field of the block, so the payload always describes what is on screen. The backend
 * accepts both and lets the id win, but a payload that says two things is a payload that can surprise.
 */
export interface PartyReferenceInput {
  partyId: string | null;
  name: string | null;
  /** Masked or not — the backend normalizes it. It is the primary key for matching an existing person. */
  cpf: string | null;
  email: string | null;
  phone: string | null;
}

/** The registered address. */
export interface CompanyAddress {
  street: string;
  number: string;
  complement: string | null;
  district: string;
  city: string;
  state: string;
  /** CEP as 8 digits, no mask. */
  zipCode: string;
  country: string;
}

/** What became of one producer in a backfill batch (19 §3). */
export interface BackfillResult {
  legacyProducerId: number;
  /** Its name in the legacy, so the report says which one without a second read. */
  name: string | null;
  outcome: 'Imported' | 'AlreadyPresent' | 'Failed';
  /** The Empresa in tmr_v2, when there is one — the report links straight to it. */
  companyId: string | null;
  gaps: RegistrationGap[];
  error: string | null;
}

/** What one batch of the legacy backfill did (`POST /companies/import/legacy/backfill`, 19 3). */
export interface BackfillBatch {
  imported: number;
  /** How many of `imported` arrived missing something — the number that decides what happens next. */
  incomplete: number;
  alreadyPresent: number;
  failed: number;
  /**
   * Cursor: pass back as `after` to continue. **Null means the end of the base was reached** — the only
   * reliable stop signal, since a batch can import nothing and still have more to read.
   */
  lastLegacyProducerId: number | null;
  results: BackfillResult[];
  /**
   * How many of the Empresas already here had an empty segment filled from what their legacy products agree
   * on (32 §12). Zero on a re-run: a segment already set is never filled again.
   */
  segmentsFilled: number;
}

/**
 * Something an Empresa's registration is missing (19 §5).
 *
 * Stable keys the backend sends; the PT-BR phrasing lives in `gaps.ts`, like all copy. A record with any
 * of `Cnpj`, `Representative` or `Address` **cannot be replicated to the legacy** until it is completed —
 * the other two are worth showing but do not block the export.
 */
export type RegistrationGap =
  | 'Cnpj'
  | 'Representative'
  | 'Address'
  | 'LegalName'
  | 'SubdomainConflict';

/**
 * One of an Empresa's managers — a User in the Identity module, referenced by id.
 *
 * One shape for both roles. They differ in meaning (who sold it vs. who looks after it afterwards) and in
 * what reaches the legacy (only the Gestor de Venda does), never in what the reader is shown.
 */
export interface CompanyManager {
  id: string;
  name: string;
}

/**
 * A candidate row in a manager picker (`GET /companies/managers`).
 *
 * Carries the e-mail that {@link CompanyManager} does not: two colleagues sharing a first name is common
 * enough that a list of names alone is not choosable. The saved record needs no such thing — by then the
 * person is already identified.
 */
export interface CompanyManagerOption {
  id: string;
  name: string;
  /** Normalized e-mail, shown under the name to tell homonyms apart. */
  email: string;
}

/**
 * The whole Empresa record (`GET /companies/{id}`). Blocks never filled in come back null.
 *
 * Three blocks the wizard used to collect are deliberately absent. The **bank account** moved to the
 * Finance module and is read from `/companies/{id}/receiving-accounts` (13); **agency** and **witness**
 * were dropped from the V2 altogether in B8.3 — what the legacy already holds stays frozen there.
 */
export interface CompanyRecord {
  id: string;
  name: string;
  legalName: string | null;
  /** Null when the record has none — a legacy producer the V1 never made type one (19 §5). */
  cnpj: string | null;
  subdomain: string;
  description: string | null;
  /** How the Empresa was won. tmr_v2 only — not replicated to the legacy. */
  acquisitionOrigin: string | null;
  /**
   * The Empresa's size band, resolved against the legacy catalogue (12 §5). Null when it has none.
   *
   * The id is always there; `code` and `description` are null for an id the catalogue no longer offers —
   * an item retired in the V1. Same shape as `representative`: a reference that no longer resolves must
   * look like one, not like an Empresa that was never classified.
   */
  classification: CompanyClassification | null;
  /** The Empresa's field of practice (32 §1), as its catalogue key; null when none is set. Never exported. */
  segment: CompanySegment | null;
  specialBilling: boolean;
  /** Gestor de Venda — who sold the Empresa; the one the export carries to the legacy. */
  salesManager: CompanyManager | null;
  /** Gestor de Pós-venda — who looks after it after the first sale. No legacy counterpart. */
  postSalesManager: CompanyManager | null;
  /** Null when nobody is on file — a legacy record with no representative (19 §5). */
  representative: PartyReference | null;
  address: CompanyAddress;
  focalContact: PartyReference | null;
  /** The business lifecycle status — the badge the detail screen leads with. */
  status: CompanyBusinessStatus;
  exportStatus: ExportStatus;
  recordOrigin: RecordOrigin;
  legacyProducerId: number | null;
  /** What this registration is missing (19 §5); empty when it is complete. */
  registrationGaps: RegistrationGap[];
  createdByOperatorId: string;
  createdAt: string;
  updatedAt: string | null;
}

/**
 * One classification an Empresa can be given — an item of the legacy's shared dictionary, group 21 (12 §1).
 *
 * The V2 reads this catalogue and does not own it: it is shared between systems and edited in the V1, so
 * there is no screen that creates or changes one.
 */
export interface CompanyClassification {
  /** The domain item id — what a write sends, and what both systems store. Stable across environments. */
  id: number;
  /** The short label: `PP`, `P`, `M`, `G`, `GG`. Null when read back for an id the catalogue dropped. */
  code: string | null;
  /** The revenue band it stands for. */
  description: string | null;
}

/**
 * The Empresa's field of practice — a catalogue the V2 owns and fixes in code (32 §5), so the wire carries
 * the key and the PT-BR label is the portal's (`segmentLabel`). Unlike the classification, it is not the
 * legacy's and never reaches it.
 */
export type CompanySegment = 'Medical' | 'Dentistry' | 'Veterinary' | 'Aesthetics' | 'MentalHealth';

/** The registration as it is submitted — the same fields for onboarding and for editing. */
export interface CompanyProfileRequest {
  name: string;
  legalName: string;
  subdomain: string;
  description: string | null;
  acquisitionOrigin: string | null;
  /**
   * The **id** of a legacy domain item, never its label (12 §5) — the same identifier the legacy stores,
   * which is what lets the export copy it across untranslated. An id outside the catalogue is refused with
   * the code `company.classification_unknown`.
   */
  classification: number | null;
  /** The segment's key, or null for none. Anything outside the catalogue is refused with a 400. */
  segment: CompanySegment | null;
  specialBilling: boolean;
  /** Gestor de Venda: the User id, validated by the backend against the Identity directory. */
  salesManagerUserId: string | null;
  /** Gestor de Pós-venda: same shape, just as optional. */
  postSalesManagerUserId: string | null;
  /** Required: an Empresa cannot exist without a legal representative. */
  representative: PartyReferenceInput;
  address: CompanyAddress;
  focalContact: PartyReferenceInput | null;
}

/**
 * `POST /companies/onboard` — the registration, the CNPJ, and the bank block.
 *
 * The last two are only here. The CNPJ identifies the Empresa and keys the export's idempotency, so it
 * is set once and never edited. The bank block is not stored on the Empresa at all: it rides the
 * onboarding event to the Finance module, which turns it into the Empresa's default receiving account
 * (13 §3). Editing it later is a write to that resource, not to this one — hence
 * {@link UpdateCompanyRequest} has neither.
 */
export interface OnboardCompanyRequest extends CompanyProfileRequest {
  cnpj: string;
  bankAccount: BankAccountDetailsInput | null;
  /** Portal V1 screens granted to the representative's automatic producer login. */
  portalPermissions: string[];
}

/** `PUT /companies/{id}` — the registration alone; the CNPJ identifies the Empresa and cannot change. */
export type UpdateCompanyRequest = CompanyProfileRequest;

/** `POST /companies/onboard` answers 202 with this while the export is still Pending. */
export interface OnboardCompanyResponse {
  companyId: string;
  subdomain: string;
  status: ExportStatus;
}

/** `PUT /companies/{id}`. An edit the legacy has not seen yet comes back Pending. */
export interface UpdateCompanyResponse {
  companyId: string;
  subdomain: string;
  exportStatus: ExportStatus;
}

/** `GET /companies/{id}/onboarding-status` — what the replication panel polls. */
export interface OnboardingStatus {
  companyId: string;
  status: ExportStatus;
  subdomain: string;
  /** Carried here too, so the panel can word "importada" vs "exportada" without a second request. */
  recordOrigin: RecordOrigin;
  legacyProducerId: number | null;
  /** Why the last export failed, for the operator to fix before reprocessing. Null unless Failed. */
  failureReason: string | null;
}

/** `PATCH /companies/{id}/status` — a lifecycle move, deliberately not part of the registration edit. */
export interface ChangeCompanyStatusRequest {
  status: CompanyBusinessStatus;
}

/** What comes back from a lifecycle move. */
export interface ChangeCompanyStatusResponse {
  companyId: string;
  status: CompanyBusinessStatus;
}

/** Error codes the Empresa's registration answers with (409), one per field it is about. */
export const COMPANY_ERROR_CODES = {
  /** An Empresa in the TMR already has this CNPJ. */
  cnpjTaken: 'company.cnpj_taken',
  /** A producer in the legacy already has this CNPJ — the detail names it, to import instead. */
  cnpjInLegacy: 'company.cnpj_in_legacy',
  /** Another Empresa already uses this subdomain. */
  subdomainTaken: 'company.subdomain_taken',
} as const;

/** `GET /companies/subdomain-suggestion?name=` — a value that is free right now, not a reservation. */
export interface SubdomainSuggestion {
  subdomain: string;
}

/**
 * A producer as it exists in the legacy system (`GET /companies/legacy?search=`). Every field is
 * nullable because the legacy table enforces almost nothing — which is exactly why the import can
 * refuse a record and name what it is missing.
 */
export interface LegacyCompanySnapshot {
  legacyProducerId: number;
  name: string | null;
  legalName: string | null;
  cnpj: string | null;
  subdomain: string | null;
  addressCity: string | null;
  addressState: string | null;
}

/** `POST /companies/import`. Idempotent: importing the same producer twice is not an error. */
export interface ImportCompanyResponse {
  companyId: string;
  legacyProducerId: number;
  subdomain: string;
  /** True when the Empresa was already in tmr_v2 and nothing was created. */
  alreadyImported: boolean;
}

/** The little a screen that is not about Empresas needs in order to name one. */
export interface CompanyRef {
  id: string;
  name: string;
  cnpj: string;
  city: string;
}

/** Narrows a list item to the reference the still-mock Contracts and Parties screens consume. */
export function toCompanyRef(company: CompanyListItem): CompanyRef {
  return {
    id: company.id,
    name: company.name,
    cnpj: company.cnpj ?? '',
    city: [company.address.city, company.address.state].filter(Boolean).join(' · '),
  };
}
